import React from 'react';
import styled, { css } from 'styled-components';
import * as CookieConsent from 'vanilla-cookieconsent';
import { nl } from 'date-fns/locale';
import { format, parseISO } from 'date-fns';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading4, Heading5 } from '~/components/atom/Typography';
import Button from '~/components/atom/Button';
import Icon from '~/components/atom/Icon';
import Portal from '~/components/molecule/Portal';
import { FOOTER_HEIGHT } from '../../index';
import Divider from '~/components/atom/Divider';
import CopyTextBlock from '~/components/molecule/CopyTextBlock';

export type CookieCategories =
  | 'necessary'
  | 'analytics'
  | 'marketing'
  | 'functional';

export type Props = {
  dataTestId?: string;
  cookiePreferences: CookieConsent.UserPreferences;
  timestamp: string;
  consentId: string;
  onClose: () => void;
  onShowPreferences: () => void;
};

const text = {
  consentId: 'Uw toestemming ID',
  timestamp: 'Datum van toestemming',
  heading: 'Cookie-instellingen',
  subheading: 'Uw huidige stand',
  buttonLabel: 'Verander uw toestemming',
  cookiePreferences: {
    analytics: 'Analyse',
    necessary: 'Noodzakelijk',
    functional: 'Functioneel',
    marketing: 'Marketing',
  },
};

const CookieSettingsModal: React.FC<Props> = ({
  cookiePreferences,
  timestamp,
  consentId,
  onClose,
  onShowPreferences,
  dataTestId,
}) => {
  const formattedDate = (() => {
    try {
      if (!timestamp) return '-';
      const parsed = parseISO(timestamp);
      if (Number.isNaN(parsed.getTime())) return '-';
      return format(parsed, 'd MMM yyyy - HH:mm:ss zzz', {
        locale: nl,
      });
    } catch {
      return '-';
    }
  })();

  return (
    <Portal root="cookie-settings-modal">
      <Container
        padding={['base']}
        direction="column"
        dataTestId={dataTestId}
        boxShadow="intense"
        border={{ radius: 'base' }}
        role="dialog"
        aria-modal="true"
      >
        <JustificationContainer
          width="100%"
          align="center"
          justification="space-between"
          padding={[null, null, 's', null]}
        >
          <Heading4 margin={[null]} color={{ group: 'primary' }}>
            {text.heading}
          </Heading4>
          <Button onClick={onClose} icon="close" ghost appearance="tertiary" />
        </JustificationContainer>

        <Divider margin={[null, null, 'xs', null]} />
        <Heading5 margin={[null]} color={{ group: 'primary' }}>
          {text.subheading}
        </Heading5>
        <JustificationContainer
          padding={['base', null]}
          direction="column"
          gap="xxs"
        >
          <JustificationContainer direction="column" gap="xxs" width="100%">
            {(
              cookiePreferences.acceptedCategories as Array<CookieCategories>
            ).map(category => (
              <ListItem key={category}>
                <Icon
                  name={category === 'necessary' ? 'lock' : 'check'}
                  color={{ group: 'primary' }}
                />
                {text.cookiePreferences[category]}
              </ListItem>
            ))}
          </JustificationContainer>
          <JustificationContainer width="100%" direction="column" gap="xxs">
            {(
              cookiePreferences.rejectedCategories as Array<CookieCategories>
            ).map(category => (
              <ListItem key={category}>
                <Icon name="close" size="m" />
                {text.cookiePreferences[category]}
              </ListItem>
            ))}
          </JustificationContainer>
        </JustificationContainer>
        <JustificationContainer
          direction="column"
          margin={[null, null, 'base', null]}
          padding={['xs']}
          border={{ radius: 'base' }}
          backgroundColor={{ group: 'tertiary', variant: 'light' }}
        >
          <CopyTextBlock
            label={text.timestamp}
            textToCopy={formattedDate}
            direction="column"
            align="start"
          />
          <CopyTextBlock
            label={text.consentId}
            textToCopy={consentId}
            direction="column"
            align="start"
          />
        </JustificationContainer>
        <Button
          onClick={() => {
            onShowPreferences();
            onClose();
          }}
          label={text.buttonLabel}
        />
      </Container>
    </Portal>
  );
};

const ListItem = styled.li(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.space('xs')};
  `,
);

const Container = styled(JustificationContainer)(
  ({ theme }) => css`
    position: fixed;
    background: ${theme.color('tertiary', 'translucent')};
    bottom: ${FOOTER_HEIGHT}px;
    left: 50%;
    transform: translateX(0);
    z-index: ${theme.getTokens().zIndex.top};
    max-width: 400px;
  `,
);

export default CookieSettingsModal;
